import { useEffect, useRef } from 'react';
import { useUserLocation } from '~/context/common/UserContext';
import {
  useDeliveryInfo,
  useDeliveryPickup,
  usePickupInfo
} from '~/context/vdp/DeliveryPickupContext';
import { useGetVehicleId } from '~/context/vdp/VehicleContext';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import { logInfo } from '~/utilities/loggers';

type HideReason = 'api_error' | 'no_scheduling_data' | 'not_purchasable';

function getHideReason(
  isNotPurchasable: boolean,
  isLoaded: boolean,
  hasError: boolean,
  hasSchedulingData: boolean
): HideReason | undefined {
  if (isNotPurchasable) return 'not_purchasable';
  if (!isLoaded) return undefined;
  if (hasError) return 'api_error';
  if (!hasSchedulingData) return 'no_scheduling_data';

  return undefined;
}

export default function useTrackDeliveryPickupVisibility(): void {
  const isNotPurchasable = useIsNotAvailableForImmediatePurchase();
  const { data, hasError, isLoaded } = useDeliveryPickup();
  const deliveryInfo = useDeliveryInfo();
  const pickupInfo = usePickupInfo();
  const { zip } = useUserLocation();
  const vehicleId = useGetVehicleId();
  const hasLoggedRef = useRef(false);

  const { soonestDelivery, soonestPickup } = data;
  const soonestDeliveryStatus = soonestDelivery.status;
  const soonestPickupStatus = soonestPickup.status;

  useEffect(() => {
    if (hasLoggedRef.current) return;

    const reason = getHideReason(
      isNotPurchasable,
      isLoaded,
      hasError,
      Boolean(deliveryInfo || pickupInfo)
    );

    if (!reason) return;

    hasLoggedRef.current = true;
    logInfo('Pickup & Delivery section hidden', {
      hasError,
      isNotPurchasable,
      reason,
      soonestDeliveryStatus,
      soonestPickupStatus,
      vehicleId,
      zip
    });
  }, [
    deliveryInfo,
    hasError,
    isLoaded,
    isNotPurchasable,
    pickupInfo,
    soonestDeliveryStatus,
    soonestPickupStatus,
    vehicleId,
    zip
  ]);
}
